class Tabs extends HTMLElement {
  async connectedCallback() {
    const defaultValue = this.getAttribute("default-value") || "";

    await Promise.all([
      customElements.whenDefined("tab-button"),
      customElements.whenDefined("tab-panel"),
    ]);

    this.injectCSS();

    this.buttons = Array.from(this.querySelectorAll("tab-button"));
    this.contents = Array.from(this.querySelectorAll("tab-panel"));

    this.buttons.forEach((btn) => {
      const value = btn.getAttribute("value");
      const panel = this.contents.find(
        (c) => c.getAttribute("value") === value
      );

      const tabId = `tab-${value}`;
      const panelId = `panel-${value}`;
      btn.setAttribute("id", tabId);
      btn.setAttribute("role", "tab");
      btn.setAttribute("aria-controls", panelId);
      btn.setAttribute("tabindex", "-1");

      if (panel) {
        panel.setAttribute("id", panelId);
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", tabId);
        panel.setAttribute("tabindex", "0");
      }
    });

    this.querySelector("tabs-list")?.setAttribute("role", "tablist");

    this.addEventListener("tab-activate", (e) => {
      const value = e.detail.value;
      this.activate(value);
    });

    this.activate(defaultValue);
  }

  activate(val) {
    this.buttons.forEach((btn) => {
      const isActive = btn.getAttribute("value") === val;
      btn.toggleAttribute("active", isActive);
      btn.setAttribute("aria-selected", isActive);
      btn.setAttribute("tabindex", isActive ? "0" : "-1");
      if (isActive) btn.focus({ preventScroll: true });
    });

    this.contents.forEach((panel) => {
      const isActive = panel.getAttribute("value") === val;
      panel.toggleAttribute("active", isActive);
      panel.setAttribute("aria-hidden", !isActive);
    });
  }

  injectCSS() {
    if (document.getElementById("tabs-style")) return;

    const style = document.createElement("style");
    style.id = "tabs-style";
    style.textContent = `
      tabs-list, .tab-list {
        display: flex;
        overflow-x: auto;
        gap: 0px;
      }

      tabs-list::-webkit-scrollbar, .tab-list::-webkit-scrollbar {
        display: none;
      }

      tabs-list * {
        flex: 1;
      }

      tab-button, .tab-button {
        flex: 1;
        white-space: nowrap;
        width: 100%;  
        height: 100%;
        min-width: 100px;
        padding: 8px;
        border-bottom: 1px solid var(--Element-black, black);
        opacity: 0.4;
        cursor: pointer;
        font-size: 14px;
        text-transform: uppercase;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        outline: none;
      }

      tab-button[active], .tab-button[active] {
        opacity: 1;
      }

      tab-panel, .tab-panel {
        display: none;
        padding-top: 20px;
      }

      tab-panel[active], .tab-panel[active] {
        display: block;
      }
    `;
    document.head.appendChild(style);
  }
}

class TabButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", () => {
      this.dispatchEvent(
        new CustomEvent("tab-activate", {
          bubbles: true,
          composed: true,
          detail: { value: this.getAttribute("value") },
        })
      );
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  customElements.define("tabs-container", Tabs);
  customElements.define("tabs-list", class extends HTMLElement {});
  customElements.define("tab-button", TabButton);
  customElements.define("tab-panel", class extends HTMLElement {});
});
